'use client';

import { c as _c } from "react/compiler-runtime";
import { formatCEP } from '@brazilian-utils/brazilian-utils';
import { Alert, Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, type ModalProps } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import { type ChangeEvent, type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import useDebounce from '@/hooks/use-debounce';
import { viacep } from '@/lib/providers/axios/api';
import removeNonNumericCharacters from '@/shared/functions/remove-non-numeric-characters';
import type { PreRegisterFormState } from '../../hooks/use-pre-register-form';
interface AddAddressModalProps extends Omit<ModalProps, 'children'> {
  preRegistration: PreRegisterFormState;
  setPreRegistration: Dispatch<SetStateAction<PreRegisterFormState>>;
}
interface ViaCepResponse {
  bairro: string;
  cep: string;
  estado: string;
  ibge: string;
  localidade: string;
  logradouro: string;
  uf: string;
  erro?: boolean | string;
}
const EMPTY_ADDRESS_FIELDS = {
  cep: '',
  address: '',
  city: '',
  ibge: '',
  neighborhood: '',
  state: '',
  stateCode: ''
} as const;
function isViaCepNotFound(data: ViaCepResponse | undefined): boolean {
  if (!data) return true;
  return data.erro === true || data.erro === 'true';
}
export default function AddAddressModal(t0) {
  const $ = _c(88);
  let isOpen;
  let onClose;
  let onOpenChange;
  let preRegistration;
  let props;
  let setPreRegistration;
  if ($[0] !== t0) {
    ({
      preRegistration,
      setPreRegistration,
      isOpen,
      onOpenChange,
      onClose,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = isOpen;
    $[2] = onClose;
    $[3] = onOpenChange;
    $[4] = preRegistration;
    $[5] = props;
    $[6] = setPreRegistration;
  } else {
    isOpen = $[1];
    onClose = $[2];
    onOpenChange = $[3];
    preRegistration = $[4];
    props = $[5];
    setPreRegistration = $[6];
  }
  const [cep, setCep] = useState(preRegistration.cep);
  const debouncedCep = useDebounce(cep, 750);
  const cepDigits = removeNonNumericCharacters(cep);
  let t1;
  if ($[7] !== setPreRegistration) {
    t1 = e => {
      const value = removeNonNumericCharacters(e.target.value);
      setCep(value);
      setPreRegistration(prev => ({
        ...prev,
        cep: value,
        ...(value.length < 8 ? EMPTY_ADDRESS_FIELDS : {})
      }));
    };
    $[7] = setPreRegistration;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleCepChange = t1;
  let t2;
  let t3;
  if ($[9] !== debouncedCep) {
    t2 = ["register-address", debouncedCep];
    t3 = async () => {
      const {
        data
      } = await viacep.get(`/ws/${debouncedCep}/json`);
      return data;
    };
    $[9] = debouncedCep;
    $[10] = t2;
    $[11] = t3;
  } else {
    t2 = $[10];
    t3 = $[11];
  }
  const t4 = debouncedCep.length === 8;
  let t5;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      staleTime: Infinity,
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: t4
    };
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const {
    data: addressData,
    isSuccess,
    isFetching,
    isError,
    isFetched
  } = useQuery(t5);
  const isCepLookupPending = cepDigits.length === 8 && (cepDigits !== debouncedCep || isFetching);
  const isCepLookupCompleted = cepDigits.length === 8 && cepDigits === debouncedCep && !isFetching && isFetched;
  let t6;
  bb0: {
    if (!isCepLookupCompleted) {
      t6 = false;
      break bb0;
    }
    if (isError) {
      t6 = true;
      break bb0;
    }
    if (!isSuccess || !addressData) {
      t6 = true;
      break bb0;
    }
    if (isViaCepNotFound(addressData)) {
      t6 = true;
      break bb0;
    }
    let t7;
    if ($[16] !== addressData.ibge) {
      t7 = addressData.ibge?.trim();
      $[16] = addressData.ibge;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = !t7;
  }
  const showCepAlert = t6;
  let t7;
  if ($[18] !== setPreRegistration) {
    t7 = () => {
      setCep("");
      setPreRegistration(_temp);
    };
    $[18] = setPreRegistration;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleClearData = t7;
  let t8;
  let t9;
  if ($[20] !== addressData || $[21] !== debouncedCep || $[22] !== isCepLookupCompleted || $[23] !== isError || $[24] !== setPreRegistration) {
    t8 = () => {
      if (!isCepLookupCompleted) {
        return;
      }
      if (isError || !addressData || isViaCepNotFound(addressData) || !addressData.ibge?.trim()) {
        setPreRegistration(prev_1 => ({
          ...prev_1,
          cep: debouncedCep,
          address: "",
          city: "",
          state: "",
          stateCode: "",
          ibge: "",
          neighborhood: ""
        }));
        return;
      }
      setPreRegistration(prev_2 => ({
        ...prev_2,
        cep: removeNonNumericCharacters(addressData.cep),
        address: addressData.logradouro,
        city: addressData.localidade,
        ibge: addressData.ibge,
        neighborhood: addressData.bairro || prev_2.neighborhood,
        state: addressData.estado,
        stateCode: addressData.uf
      }));
    };
    t9 = [addressData, debouncedCep, isCepLookupCompleted, isError, setPreRegistration];
    $[20] = addressData;
    $[21] = debouncedCep;
    $[22] = isCepLookupCompleted;
    $[23] = isError;
    $[24] = setPreRegistration;
    $[25] = t8;
    $[26] = t9;
  } else {
    t8 = $[25];
    t9 = $[26];
  }
  useEffect(t8, t9);
  const isAddressFieldsDisabled = cepDigits.length < 8 || isCepLookupPending || showCepAlert;
  const canComplete = cepDigits.length === 8 && !showCepAlert && Boolean(preRegistration.ibge?.trim()) && Boolean(preRegistration.address?.trim()) && Boolean(preRegistration.neighborhood?.trim()) && Boolean(preRegistration.city?.trim()) && Boolean(preRegistration.state?.trim()) && Boolean(preRegistration.stateCode?.trim());
  const T0 = Modal;
  const t10 = "blur";
  const t11 = "2xl";
  const T1 = ModalContent;
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ModalHeader>Busque seu endereço</ModalHeader>;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const T2 = ModalBody;
  const t13 = "gap-4";
  const t14 = "flex items-center gap-2";
  const T3 = Input;
  const t15 = true;
  const t16 = true;
  const t17 = cepDigits ? formatCEP(cepDigits) : "";
  let t18;
  if ($[28] !== T3 || $[29] !== handleCepChange || $[30] !== isCepLookupPending || $[31] !== t17) {
    t18 = <T3 isRequired={t15} required={t16} value={t17} onChange={handleCepChange} variant="underlined" type="text" placeholder="Digite seu CEP" name="cep" label="CEP" isDisabled={isCepLookupPending} autoComplete="off" />;
    $[28] = T3;
    $[29] = handleCepChange;
    $[30] = isCepLookupPending;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== setPreRegistration) {
    t19 = e_0 => setPreRegistration(prev_3 => ({
      ...prev_3,
      city: e_0.target.value
    }));
    $[33] = setPreRegistration;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== isAddressFieldsDisabled || $[36] !== preRegistration.city || $[37] !== t19) {
    t20 = <Input isRequired={true} required={true} value={preRegistration.city} onChange={t19} variant="underlined" type="text" placeholder="Digite sua cidade" name="city" label="Cidade" isDisabled={isAddressFieldsDisabled} />;
    $[35] = isAddressFieldsDisabled;
    $[36] = preRegistration.city;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t18 || $[40] !== t20) {
    t21 = <div className={t14}>{t18}{t20}</div>;
    $[39] = t18;
    $[40] = t20;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== handleClearData || $[43] !== showCepAlert) {
    t22 = showCepAlert && <Alert variant="faded" color="warning" className="grow-0" classNames={{
      base: "items-start sm:flex-row flex-col",
      title: "text-base",
      description: "text-sm"
    }} title={"CEP n\xE3o encontrado"} description={"O CEP informado n\xE3o existe ou n\xE3o retornou dados v\xE1lidos. O endere\xE7o n\xE3o ser\xE1 adicionado."}><Button className="self-end" variant="flat" color="default" onPress={handleClearData}>Limpar campos</Button></Alert>;
    $[42] = handleClearData;
    $[43] = showCepAlert;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== setPreRegistration) {
    t23 = e_1 => setPreRegistration(prev_4 => ({
      ...prev_4,
      address: e_1.target.value
    }));
    $[45] = setPreRegistration;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== isAddressFieldsDisabled || $[48] !== preRegistration.address || $[49] !== t23) {
    t24 = <Input isRequired={true} required={true} value={preRegistration.address} onChange={t23} variant="underlined" type="text" placeholder={"Digite seu endere\xE7o"} name="address" label={"Endere\xE7o"} isDisabled={isAddressFieldsDisabled} />;
    $[47] = isAddressFieldsDisabled;
    $[48] = preRegistration.address;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== setPreRegistration) {
    t25 = e_2 => setPreRegistration(prev_5 => ({
      ...prev_5,
      state: e_2.target.value
    }));
    $[51] = setPreRegistration;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== isAddressFieldsDisabled || $[54] !== preRegistration.state || $[55] !== t25) {
    t26 = <Input isRequired={true} required={true} value={preRegistration.state} onChange={t25} variant="underlined" type="text" placeholder="Digite seu estado" name="state" label="Estado" isDisabled={isAddressFieldsDisabled} />;
    $[53] = isAddressFieldsDisabled;
    $[54] = preRegistration.state;
    $[55] = t25;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== setPreRegistration) {
    t27 = e_3 => setPreRegistration(prev_6 => ({
      ...prev_6,
      neighborhood: e_3.target.value
    }));
    $[57] = setPreRegistration;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== isAddressFieldsDisabled || $[60] !== preRegistration.neighborhood || $[61] !== t27) {
    t28 = <Input isRequired={true} required={true} value={preRegistration.neighborhood} onChange={t27} variant="underlined" type="text" placeholder="Digite seu bairro" name="neighborhood" label="Bairro" isDisabled={isAddressFieldsDisabled} />;
    $[59] = isAddressFieldsDisabled;
    $[60] = preRegistration.neighborhood;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== t26 || $[64] !== t28) {
    t29 = <div className="flex items-center gap-2">{t26}{t28}</div>;
    $[63] = t26;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== T2 || $[67] !== t21 || $[68] !== t22 || $[69] !== t24 || $[70] !== t29) {
    t30 = <T2 className={t13}>{t21}{t22}{t24}{t29}</T2>;
    $[66] = T2;
    $[67] = t21;
    $[68] = t22;
    $[69] = t24;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  const t31 = !canComplete;
  let t32;
  if ($[72] !== isCepLookupPending || $[73] !== onClose || $[74] !== t31) {
    t32 = <ModalFooter><Button color="success" variant="flat" isDisabled={t31} isLoading={isCepLookupPending} onPress={onClose}>Concluir</Button></ModalFooter>;
    $[72] = isCepLookupPending;
    $[73] = onClose;
    $[74] = t31;
    $[75] = t32;
  } else {
    t32 = $[75];
  }
  let t33;
  if ($[76] !== T1 || $[77] !== t12 || $[78] !== t30 || $[79] !== t32) {
    t33 = <T1>{t12}{t30}{t32}</T1>;
    $[76] = T1;
    $[77] = t12;
    $[78] = t30;
    $[79] = t32;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== T0 || $[82] !== isOpen || $[83] !== onClose || $[84] !== onOpenChange || $[85] !== props || $[86] !== t33) {
    t34 = <T0 backdrop={t10} size={t11} isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} {...props}>{t33}</T0>;
    $[81] = T0;
    $[82] = isOpen;
    $[83] = onClose;
    $[84] = onOpenChange;
    $[85] = props;
    $[86] = t33;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  return t34;
}
function _temp(prev_0) {
  return {
    ...prev_0,
    ...EMPTY_ADDRESS_FIELDS
  };
}